import { FC, useState } from "react";
import { Divider, Form } from "antd";
import { IProduct, IProductPriceDetail, IUpdateProduct } from "../types";
import { updateProduct } from "../api";
import { Body2, Button2, Label } from "../../../components/Typography";
import { useTranslation } from "react-i18next";
import { LMNewButton } from "../../../components/LMNewButton";
import { LMNewInput } from "../../../components/LMNewInput";
import { LMIconButton } from "../../../components/LMIconButton";
import { IconTrash } from "../../../assets";
import { useAppNotifications } from "../../../components/LMNotifications";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../app/store";
import { fetchProductDetails } from "../slice";
import styles from "./ProductPrices.module.css";

interface Props {
  product: IProduct;
  onClose: () => void;
  onSuccess: () => void;
  onError: () => void;
}

interface FormValues {
  price: number;
  amount: number;
  description: string;
}

export const ProductPrices: FC<Props> = ({
  product,
  onClose,
  onSuccess,
  onError,
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const { t } = useTranslation("productsPortfolio");
  const { t: tLayout } = useTranslation("layout");
  const { contextHolder } = useAppNotifications();
  const dispatch = useDispatch<AppDispatch>();

  const handleSubmit = async (values: FormValues) => {
    let updatedSublines = product.price?.default?.sublines || [];

    if (values.amount && values.description?.trim()) {
      const newSubline = {
        amount: Number(values.amount),
        description: values.description.trim(),
      };

      updatedSublines = [...updatedSublines, newSubline];
    }

    const priceDetail: IProductPriceDetail = {
      price: Number(values.price),
      sublines: updatedSublines,
    };

    const payload: Pick<IUpdateProduct, "price"> = {
      price: { default: priceDetail },
    };

    setLoading(true);
    try {
      await updateProduct(product.id, payload);
      onSuccess();
      form.resetFields(["amount", "description"]);
      await dispatch(fetchProductDetails(product.id)).unwrap();
    } catch (err) {
      console.error("Error adding subline:", err);
      onError();
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubline = async (index: number) => {
    if (!product.price?.default) return;

    const updatedSublines =
      product.price.default.sublines?.filter((_, i) => i !== index) || [];

    const priceDetail: IProductPriceDetail = {
      price: product.price.default.price,
      sublines: updatedSublines,
    };

    const payload: Pick<IUpdateProduct, "price"> = {
      price: { default: priceDetail },
    };

    setLoading(true);
    try {
      await updateProduct(product.id, payload);
      onSuccess();
      await dispatch(fetchProductDetails(product.id)).unwrap();
    } catch (error) {
      onError();
      console.error("Error deleting subline:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {contextHolder}
      <Form
        form={form}
        onFinish={handleSubmit}
        initialValues={{ price: product.price?.default?.price }}
        className={styles.form}
      >
        <Label className={styles.label}>{t("defaultPrice")}</Label>
        <Form.Item
          name="price"
          rules={[{ required: true, message: t("requiredMessage") }]}
        >
          <LMNewInput placeholder={t("enterPriceValue")} />
        </Form.Item>
        <Divider />
        <div className={styles.content}>
          {product.price?.default?.sublines?.map((subline, index) => (
            <div
              className={styles.item}
              key={`${subline.amount}-${subline.description}-${index}`}
            >
              <div className={styles["item-content"]}>
                <div className={styles.line}>
                  <Body2 className={styles["item-title"]}>
                    {t("priceDescription")}:
                  </Body2>
                  <Button2 className={styles["item-value"]}>
                    {subline.description}
                  </Button2>
                </div>
                <div className={styles.line}>
                  <Body2 className={styles["item-title"]}>
                    {t("priceAmount")}:
                  </Body2>
                  <Button2 className={styles["item-value"]}>
                    {subline.amount}
                  </Button2>
                </div>
              </div>
              <LMIconButton onClick={() => handleDeleteSubline(index)}>
                <IconTrash />
              </LMIconButton>
            </div>
          ))}
        </div>
        <Label className={styles.label}>{t("addNewSubline")}</Label>
        <Form.Item name="description">
          <LMNewInput placeholder={t("priceDescription")} />
        </Form.Item>
        <Form.Item name="amount">
          <LMNewInput placeholder={t("priceAmount")} />
        </Form.Item>
      </Form>

      <div className={styles.buttons}>
        <LMNewButton
          color="default"
          onClick={onClose}
          className={styles.button}
        >
          {t("cancel")}
        </LMNewButton>

        <LMNewButton disabled={loading} onClick={() => form.submit()}>
          {loading ? tLayout("pleaseWait") : t("updatePrice")}
        </LMNewButton>
      </div>
    </div>
  );
};
